(() => {
  if (customElements.get('vics-art-discovery')) return;
  const storageKey = 'vics-artwork-history-v1';
  const handleFromURL = (value) => {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) return null;
      return decodeURIComponent(url.pathname.match(/\/products\/([^/]+)\/?$/)?.[1] || '') || null;
    } catch { return null; }
  };
  customElements.define('vics-art-discovery', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.pairs = this.querySelector('[data-discovery-pairs]');
      this.recent = this.querySelector('[data-discovery-recent]');
      this.current = this.dataset.current;
      this.cleanup = [];
      this.cards = [];
      this.history = this.recordVisit();
      this.pairCards = [...this.pairs.querySelectorAll('[data-artwork-handle]')];
      this.native = document.querySelector('product-recommendations');
      if (this.native) {
        this.nativeObserver = new MutationObserver(() => {
          this.refresh();
        });
        this.nativeObserver.observe(this.native, { childList: true, subtree: true });
      }
      this.refresh();
      // History fetches begin near the shelf, not during the product's first paint.
      this.intersection = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.intersection.disconnect();
          this.setupCarousel(this.pairs);
          this.setupCarousel(this.recent);
          this.loadHistory();
        }
      }, { rootMargin: '600px' });
      this.intersection.observe(this);
      addEventListener('pageshow', this.onPageShow = event => {
        if (event.persisted) { this.history = this.recordVisit(); this.loadHistory(); }
      }, { signal: this.abort.signal });
    }
    recordVisit() {
      let visits = [];
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
        if (Array.isArray(stored)) visits = stored.filter(item => item &&
          typeof item.path === 'string' && handleFromURL(item.path) === item.handle &&
          item.handle !== this.current).slice(0, 12);
        const path = this.dataset.currentUrl;
        if (handleFromURL(path) === this.current) {
          localStorage.setItem(storageKey, JSON.stringify([{ handle: this.current, path }, ...visits].slice(0, 13)));
        }
      } catch { /* The gallery still works if browser storage is unavailable. */ }
      return [...new Map(visits.map(item => [item.handle, item])).values()];
    }
    nativeHandles() {
      return new Set([...this.native?.querySelectorAll('.resource-list__slide a[href]') || []]
        .map(link => handleFromURL(link.href)).filter(Boolean));
    }
    refresh() {
      if (!this.isConnected) return;
      const used = this.nativeHandles();
      used.add(this.current);
      const pairHandles = new Set();
      let pairsCount = 0;
      for (const card of this.pairCards) {
        const handle = card.dataset.artworkHandle;
        const show = !used.has(handle) && !pairHandles.has(handle) && pairsCount < Number(this.dataset.pairsLimit);
        card.hidden = !show;
        if (show) { pairHandles.add(handle); pairsCount++; }
      }
      this.pairs.hidden = pairsCount < 2;
      if (!this.pairs.hidden) pairHandles.forEach(handle => used.add(handle));
      let recentCount = 0;
      const recentTrack = this.recent.querySelector('.vics-series__track');
      for (const card of this.cards) {
        const handle = card.dataset.artworkHandle;
        const show = !used.has(handle) && recentCount < Number(this.dataset.recentLimit);
        card.hidden = !show;
        if (show) { used.add(handle); recentCount++; }
        if (card.parentElement !== recentTrack) recentTrack.append(card);
      }
      this.recent.hidden = recentCount < 2;
      this.dispatchEvent(new Event('discovery:updated'));
    }
    async loadHistory() {
      if (this.loading || this.history.length < 2) return;
      this.loading = true;
      const visits = this.history.slice(0, 12);
      const results = new Array(visits.length);
      let cursor = 0;
      const worker = async () => {
        while (cursor < visits.length) {
          const index = cursor++;
          try {
            const url = new URL(visits[index].path, location.origin);
            url.searchParams.set('section_id', 'vics-discovery-product-card');
            const response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' });
            if (!response.ok) continue;
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = doc.querySelector('article[data-artwork-handle]');
            if (card?.dataset.artworkHandle === visits[index].handle) results[index] = document.importNode(card, true);
          } catch { /* Removed, unavailable or failed products do not become stale cards. */ }
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, visits.length) }, worker));
      if (!this.isConnected) return;
      this.recent.querySelector('.vics-series__track').replaceChildren();
      this.cards = results.filter(Boolean);
      this.loading = false;
      this.refresh();
    }
    setupCarousel(shelf) {
      const track = shelf.querySelector('.vics-series__track');
      const previous = shelf.querySelector('[data-discovery-prev]');
      const next = shelf.querySelector('[data-discovery-next]');
      const update = () => {
        previous.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      const move = direction => track.scrollBy({
        left: direction * (track.clientWidth + parseFloat(getComputedStyle(track).gap)),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      previous.addEventListener('click', () => move(-1), { signal: this.abort.signal });
      next.addEventListener('click', () => move(1), { signal: this.abort.signal });
      track.addEventListener('scroll', update, { passive: true, signal: this.abort.signal });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal: this.abort.signal });
      this.addEventListener('discovery:updated', update, { signal: this.abort.signal });
      const observer = new ResizeObserver(update);
      observer.observe(track);
      this.cleanup.push(() => observer.disconnect());
      update();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.nativeObserver?.disconnect();
      this.intersection?.disconnect();
      this.cleanup?.forEach(fn => fn());
    }
  });
})();
